/**
 * "Atributos": filtros pelos atributos próprios (ex.: controlado, termolábil, antibiótico) num painel que abre
 * pelo botão, para não encher a barra de filtros. Vai na URL como ?attrs=controlado:true;antibiotico:false.
 * Inclui os atributos padrão do sistema (berytus_*, do catálogo mestre, somente leitura), num grupo à parte.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SlidersHorizontal, X } from 'lucide-react';
import { SYSTEM_ATTRS, type AttributeDef } from '@berytus/contracts';
import { api } from '../api.ts';

export function parseAttrs(s: string | null): Map<string, string> {
  const m = new Map<string, string>();
  for (const part of (s ?? '').split(';')) {
    const i = part.indexOf(':');
    if (i > 0) m.set(part.slice(0, i), part.slice(i + 1));
  }
  return m;
}
const serialize = (m: Map<string, string>) => [...m].filter(([, v]) => v !== '').map(([k, v]) => `${k}:${v}`).join(';');

export function useAttributeDefs() {
  return useQuery({ queryKey: ['attributes'], queryFn: () => api<AttributeDef[]>('/catalog/attributes'), staleTime: 5 * 60_000 });
}

export function AttrFilterButton({ params, set }: { params: URLSearchParams; set: (p: Record<string, string | null>) => void }) {
  const defs = useAttributeDefs();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const current = parseAttrs(params.get('attrs'));
  const own = (defs.data ?? []).filter((d) => d.filterable && d.dataType !== 'date');
  const system = SYSTEM_ATTRS.map((a) => ({ key: a.key, label: `${a.label} (Berytus)`, dataType: a.dataType, options: [] as string[] }));
  const list = [...own, ...system];
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  if (!list.length) return null;
  const change = (key: string, value: string) => {
    const m = new Map(current);
    if (value) m.set(key, value); else m.delete(key);
    set({ attrs: serialize(m) || null, page: null });
  };
  const label = (k: string) => list.find((d) => d.key === k)?.label ?? k;
  const shown = (v: string) => (v === 'true' ? 'sim' : v === 'false' ? 'não' : v);
  return (
    <div ref={box} style={{ position: 'relative', display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      <button className="btn" type="button" aria-expanded={open} aria-pressed={current.size > 0} onClick={() => setOpen((o) => !o)}>
        <SlidersHorizontal size={16} /> Atributos{current.size ? ` · ${current.size}` : ''}
      </button>
      {[...current].map(([k, v]) => (
        <span key={k} className="tag info" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {label(k)}: {shown(v)}
          <button type="button" aria-label={`Tirar ${label(k)}`} onClick={() => change(k, '')}
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', display: 'inline-flex', color: 'inherit' }}><X size={12} /></button>
        </span>
      ))}
      {open && (
        <div role="dialog" aria-label="Filtrar por atributos" className="panel"
          style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 30, width: 300, padding: 14, display: 'grid', gap: 10, boxShadow: '0 8px 28px rgba(0,0,0,.14)' }}>
          {list.map((d, i) => (
            <Fragment key={d.key}>{i === own.length && <div className="small muted" style={{ borderTop: '1px solid var(--line)', paddingTop: 6 }}>Do sistema (catálogo mestre)</div>}
            <label style={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 8, alignItems: 'center' }}>
              <span>{d.label}</span>
              {d.dataType === 'boolean' ? (
                <select className="select" value={current.get(d.key) ?? ''} onChange={(e) => change(d.key, e.target.value)}>
                  <option value="">Qualquer</option>
                  <option value="true">Sim</option>
                  <option value="false">Não</option>
                </select>
              ) : d.dataType === 'enum' ? (
                <select className="select" value={current.get(d.key) ?? ''} onChange={(e) => change(d.key, e.target.value)}>
                  <option value="">Qualquer</option>
                  <option value="*">Preenchido</option>
                  {d.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input className="input" placeholder="Qualquer" defaultValue={current.get(d.key) ?? ''}
                  onBlur={(e) => change(d.key, e.target.value.trim().replace(/;/g, ''))}
                  onKeyDown={(e) => { if (e.key === 'Enter') change(d.key, (e.target as HTMLInputElement).value.trim().replace(/;/g, '')); }} />
              )}
            </label></Fragment>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="small muted">Kits seguem o produto base.</span>
            {current.size > 0 && <button className="btn sm" type="button" onClick={() => set({ attrs: null, page: null })}>Limpar</button>}
          </div>
        </div>
      )}
    </div>
  );
}
